/**
 * Safe storage wrapper.
 * - Wraps localStorage in try/catch so a restricted/sandboxed iframe (e.g.
 *   Safari ITP, third-party iframe with storage access denied) never throws.
 * - Falls back to an in-memory Map so the rest of the app keeps working.
 * - API: get(key), set(key, value), remove(key), available()
 */
const memory = new Map();

function lsAvailable() {
  try {
    if (typeof window === "undefined") return false;
    const probe = "__ttp_storage_probe__";
    window.localStorage.setItem(probe, "1");
    window.localStorage.removeItem(probe);
    return true;
  } catch (_) {
    return false;
  }
}

const useLS = lsAvailable();

export const safeStorage = {
  available: () => useLS,
  get(key) {
    if (useLS) {
      try {
        return window.localStorage.getItem(key);
      } catch (_) {
        return memory.has(key) ? memory.get(key) : null;
      }
    }
    return memory.has(key) ? memory.get(key) : null;
  },
  set(key, value) {
    if (useLS) {
      try {
        window.localStorage.setItem(key, value);
        return true;
      } catch (_) {
        memory.set(key, value);
        return false;
      }
    }
    memory.set(key, value);
    return false;
  },
  remove(key) {
    if (useLS) {
      try {
        window.localStorage.removeItem(key);
      } catch (_) {
        memory.delete(key);
      }
    } else {
      memory.delete(key);
    }
  },
};

export default safeStorage;
